export const FORM_TABS = {
  BUILD: "build",
  LIVE: "live",
  DEMO: "demo",
  UNZIPPD: "unzippd",
};

export const FORM_TAB_VALUES = new Set(Object.values(FORM_TABS));

export const getFormListTabFromSearch = (search) => {
  const params =
    search instanceof URLSearchParams
      ? search
      : new URLSearchParams(search || "");
  const tab = params.get("tab");
  return FORM_TAB_VALUES.has(tab) ? tab : FORM_TABS.BUILD;
};

export const buildFormsListPath = (basePath, tab) => {
  const path = `/${basePath}/forms`;
  if (!tab || tab === FORM_TABS.BUILD) return path;
  return `${path}?tab=${encodeURIComponent(tab)}`;
};

const LIVE_STATUSES = new Set(["active", "complete", "finished", "completed"]);

export const normalizeFormStatus = (status) =>
  String(status || "")
    .toLowerCase()
    .replace(/,/g, "")
    .trim();

export const normalizeFormType = (type) =>
  String(type || "")
    .toLowerCase()
    .trim();

export const normalizeOrganizationName = (organization) => {
  if (typeof organization === "string") {
    return organization.trim().toLowerCase();
  }

  return String(
    organization?.name ||
      organization?.organization_name ||
      organization?.title ||
      "",
  )
    .trim()
    .toLowerCase();
};

export const getFormOrganizationDisplayName = (form) => {
  if (form?.demo_organization_name) {
    return String(form.demo_organization_name).trim();
  }

  const organization = form?.organization;

  if (typeof organization === "string") {
    const trimmed = organization.trim();
    if (trimmed) return trimmed;
  }

  if (organization && typeof organization === "object") {
    const name =
      organization.name ||
      organization.organization_name ||
      organization.title;
    if (name) return String(name).trim();
  }

  if (form?.organization_name) {
    return String(form.organization_name).trim();
  }

  return "";
};

export const pickMediaUrl = (value) => {
  if (value == null) {
    return "";
  }

  if (typeof value === "string") {
    return value.trim();
  }

  if (typeof value === "object") {
    return pickMediaUrl(value.url || value.file || value.src || value.path);
  }

  return "";
};

export const getFormOrganizationLogoUrl = (form) => {
  if (normalizeFormType(form?.type) === "demo") {
    if (
      form &&
      Object.prototype.hasOwnProperty.call(form, "demo_organization_logo")
    ) {
      return pickMediaUrl(form.demo_organization_logo);
    }

    const legacyCandidates = [form?.demo_organization_logo, form?.logo, form?.image];

    for (const candidate of legacyCandidates) {
      const url = pickMediaUrl(candidate);
      if (url) {
        return url;
      }
    }

    return "";
  }

  const candidates = [
    form?.demo_organization_logo,
    form?.logo,
    form?.image,
    form?.organization?.logo,
    form?.organization?.image,
  ];

  for (const candidate of candidates) {
    const url = pickMediaUrl(candidate);
    if (url) {
      return url;
    }
  }

  return "";
};

/** Extract form array from /form/all (and org variants) API payloads. */
export const normalizeFormsListResponse = (
  formsData,
  isOrganizationContext = false,
) => {
  const root = formsData?.data ?? formsData;
  if (!root) {
    return [];
  }

  if (isOrganizationContext) {
    if (Array.isArray(root.all_forms)) {
      return root.all_forms;
    }
  }

  if (Array.isArray(root)) {
    return root;
  }

  if (Array.isArray(root.data)) {
    return root.data;
  }

  if (Array.isArray(root.forms)) {
    return root.forms;
  }

  if (Array.isArray(root.all_forms)) {
    return root.all_forms;
  }

  return [];
};

/** Ensure demo forms expose demo_organization_* on the record object itself. */
export const withResolvedDemoFields = (form) => {
  if (!form || !isDemoFormType(form)) {
    return form;
  }

  const demoName = getFormOrganizationDisplayName(form);
  const hasExplicitDemoLogo = Object.prototype.hasOwnProperty.call(
    form,
    "demo_organization_logo",
  );
  const demoLogo = hasExplicitDemoLogo
    ? pickMediaUrl(form.demo_organization_logo)
    : getFormOrganizationLogoUrl(form);

  return {
    ...form,
    demo_organization_name: demoName || null,
    demo_organization_logo: demoLogo || null,
  };
};

/** Merge list row + GET /form/:id so demo fields from form/all are not dropped. */
export const resolveFormEditSource = (listRecord, detailsRecord) => {
  if (!listRecord && !detailsRecord) {
    return null;
  }

  if (!detailsRecord) {
    return withResolvedDemoFields(listRecord);
  }

  if (!listRecord) {
    return detailsRecord;
  }

  const merged = { ...listRecord, ...detailsRecord };

  if (!isDemoFormType(merged)) {
    return merged;
  }

  // Prefer /form/all row over GET /form/:id — details often omit demo logo fields.
  const demoName =
    getFormOrganizationDisplayName(listRecord) ||
    getFormOrganizationDisplayName(detailsRecord) ||
    "";

  const listHasExplicitLogo =
    listRecord &&
    Object.prototype.hasOwnProperty.call(listRecord, "demo_organization_logo");
  const detailsHasExplicitLogo =
    detailsRecord &&
    Object.prototype.hasOwnProperty.call(detailsRecord, "demo_organization_logo");

  const demoLogo = listHasExplicitLogo
    ? pickMediaUrl(listRecord.demo_organization_logo)
    : detailsHasExplicitLogo
      ? pickMediaUrl(detailsRecord.demo_organization_logo)
      : getFormOrganizationLogoUrl(listRecord) ||
        getFormOrganizationLogoUrl(detailsRecord) ||
        "";

  return withResolvedDemoFields({
    ...merged,
    demo_organization_name: demoName || null,
    demo_organization_logo: demoLogo || null,
    ...(demoLogo
      ? { image: demoLogo, logo: demoLogo }
      : { image: "", logo: "" }),
  });
};

export const isDemoFormType = (form) =>
  normalizeFormType(form?.type) === "demo";

export const isLiveStatus = (status) =>
  LIVE_STATUSES.has(normalizeFormStatus(status));

export const isActiveFormStatus = (status) =>
  normalizeFormStatus(status) === "active";

export const isCompletedFormStatus = (status) => {
  const normalized = normalizeFormStatus(status);
  return (
    normalized === "complete" ||
    normalized === "finished" ||
    normalized === "completed"
  );
};

export const formMatchesStatusFilter = (form, selectedStatus) => {
  const normalizedSelectedStatus = normalizeFormStatus(selectedStatus);
  if (!normalizedSelectedStatus) return true;

  if (isCompletedFormStatus(normalizedSelectedStatus)) {
    return isCompletedFormStatus(form?.status);
  }

  return normalizeFormStatus(form?.status) === normalizedSelectedStatus;
};

/** Ended from Live via deactivate — still shown on Live / Demo tabs. */
export const isInactiveStatus = (status) =>
  normalizeFormStatus(status) === "not_active";

export const isUnzippdOrganization = (form) =>
  normalizeOrganizationName(
    getFormOrganizationDisplayName(form) || form?.organization,
  ) === "unzippd";

/** Reserved for a future Internal Research tab. */
export const isInternalResearchForm = (form) =>
  isLiveStatus(form?.status) &&
  !isDemoFormType(form) &&
  isUnzippdOrganization(form);

/**
 * Build — draft forms only (any type), not yet live.
 * Demo — demo forms that are live or ended (inactive).
 * Live — live or ended non-demo forms (excluding Unzippd org).
 * Unzippd — live or ended non-demo forms for the Unzippd organisation.
 */
export const formMatchesTab = (form, tab) => {
  const status = normalizeFormStatus(form?.status);
  const live = isLiveStatus(status);
  const ended = isInactiveStatus(status);
  const demo = isDemoFormType(form);
  const unzippd = isUnzippdOrganization(form);

  if (tab === FORM_TABS.BUILD) {
    return status === "draft";
  }

  if (tab === FORM_TABS.DEMO) {
    return demo && (live || ended);
  }

  if (tab === FORM_TABS.LIVE) {
    return (live || ended) && !demo && !unzippd;
  }

  if (tab === FORM_TABS.UNZIPPD) {
    return (live || ended) && !demo && unzippd;
  }

  return false;
};

/** Live / Demo / Unzippd tabs: active → completed → inactive. */
export const sortFormsForDisplayTab = (forms, tab) => {
  if (
    tab !== FORM_TABS.LIVE &&
    tab !== FORM_TABS.DEMO &&
    tab !== FORM_TABS.UNZIPPD
  ) {
    return forms;
  }

  const statusRank = (status) => {
    if (isActiveFormStatus(status)) return 0;
    if (isCompletedFormStatus(status)) return 1;
    if (isInactiveStatus(status)) return 2;

    return 3;
  };

  return [...forms].sort(
    (a, b) => statusRank(a?.status) - statusRank(b?.status),
  );
};

export const shouldCountFormInStats = (form) => !isDemoFormType(form);

export const getFormsCountStats = (forms) => {
  const countableForms = Array.isArray(forms) ? forms : [];
  const total = countableForms.length;
  const active = countableForms.filter((form) =>
    isActiveFormStatus(form?.status),
  ).length;
  const complete = countableForms.filter((form) =>
    isCompletedFormStatus(form?.status),
  ).length;
  const totalResponses = countableForms.reduce(
    (sum, form) =>
      sum + Number(form?.total_responses || form?.responses_received || 0),
    0,
  );

  return {
    total,
    active,
    complete,
    totalResponses,
    responseRate: total ? Number(((complete / total) * 100).toFixed(1)) : 0,
  };
};

const FORM_LIST_TABS = [
  FORM_TABS.BUILD,
  FORM_TABS.LIVE,
  FORM_TABS.DEMO,
  FORM_TABS.UNZIPPD,
];

/** Combined Forms page overview: Building + Live + Demo + Unzippd. */
export const getFormsListOverviewStats = (forms) => {
  const list = Array.isArray(forms) ? forms : [];
  const visibleForms = list.filter((form) =>
    FORM_LIST_TABS.some((tab) => formMatchesTab(form, tab)),
  );

  return getFormsCountStats(visibleForms);
};
